import {ActionIcon, Input, Popover} from '@mantine/core';
import {Calendar, TimeInput} from '@mantine/dates';
import type React from 'react';
import {MdClose} from 'react-icons/md';

// TimeInputのstep値（秒単位）: 600秒 = 10分間隔
const TIME_INPUT_STEP_SECONDS = 600;

export type ModalDateInputPresenterProps = {
  // 表示用の状態
  inputText: string;
  calendarOpened: boolean;
  calendarDate: Date | null;
  timeValue: string;
  // 設定
  withTime: boolean;
  disabled: boolean;
  error?: string;
  showClearButton: boolean;
  /** SP時はキーボードを開かないようにreadOnlyにする */
  readOnly?: boolean;
  /** Inputに適用するクラス名。幅の指定などに使用。指定しない場合はflex-1が適用される */
  className?: string;
  // イベントハンドラ
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputBlur: () => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onInputMouseDown: (e: React.MouseEvent) => void;
  onCalendarSelect: (date: Date) => void;
  onTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: (e: React.MouseEvent) => void;
  onPopoverClose: () => void;
  // ref
  inputRef: React.RefObject<HTMLInputElement>;
};

export const ModalDateInputPresenter: React.FC<
  ModalDateInputPresenterProps
> = ({
  inputText,
  calendarOpened,
  calendarDate,
  timeValue,
  withTime,
  disabled,
  error,
  showClearButton,
  className,
  onInputChange,
  onInputBlur,
  onInputKeyDown,
  onInputMouseDown,
  onCalendarSelect,
  onTimeChange,
  onClear,
  onPopoverClose,
  inputRef,
  readOnly,
}) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <Popover
          opened={calendarOpened}
          onClose={onPopoverClose}
          position="bottom-start"
          withinPortal
          trapFocus={false}
          classNames={{
            dropdown: 'z-[1000]',
          }}
        >
          <Popover.Target>
            <Input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onKeyDown={onInputKeyDown}
              onMouseDown={onInputMouseDown}
              placeholder={withTime ? 'yyyy/mm/dd hh:mm' : 'yyyy/mm/dd'}
              size="sm"
              className={className || 'flex-1'}
              classNames={{
                input: `text-sm w-full border border-line-2 rounded-[24px] ${error ? 'border-notification-1' : ''}`,
                wrapper: className || 'flex-1',
              }}
              disabled={disabled}
              readOnly={readOnly}
            />
          </Popover.Target>
          <Popover.Dropdown>
            <div className="flex flex-col gap-8">
              <Calendar
                date={calendarDate ?? undefined}
                __onDayClick={(_, date) => onCalendarSelect(date)}
                placeholder=""
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
              {withTime && (
                <div className="flex items-center gap-8 px-8 pb-8">
                  <span className="text-sm text-black-2">時刻:</span>
                  <TimeInput
                    value={timeValue}
                    onChange={onTimeChange}
                    size="xs"
                    step={TIME_INPUT_STEP_SECONDS}
                    classNames={{
                      input: 'text-sm w-[80px]',
                    }}
                  />
                </div>
              )}
            </div>
          </Popover.Dropdown>
        </Popover>
        {showClearButton && (
          <ActionIcon
            size="xs"
            variant="transparent"
            onClick={onClear}
            className="shrink-0 hover:opacity-60"
            title="クリア"
          >
            <MdClose size={14} />
          </ActionIcon>
        )}
      </div>
      {error && <p className="text-xs text-notification-1">{error}</p>}
    </div>
  );
};
